import React, { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";

const STATUS_COLORS = {
    pending: "text-amber-300 bg-amber-500/10 border-amber-500/30",
    approved: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
    rejected: "text-red-300 bg-red-500/10 border-red-500/30",
};

export default function AdminPartnersPage() {
    const [apps, setApps] = useState([]);
    const [jobsData, setJobsData] = useState({ jobs: [], total_owed: 0, total_collected: 0 });
    const [selected, setSelected] = useState(null);
    const [selectedQuality, setSelectedQuality] = useState(null);
    const [tab, setTab] = useState("applications");
    const [error, setError] = useState("");
    const [reminding, setReminding] = useState(null);

    async function load() {
        try {
            const [a, j] = await Promise.all([
                http.get("/admin/partners"),
                http.get("/admin/partner-jobs"),
            ]);
            setApps(a.data); setJobsData(j.data);
            if (selected) {
                const cur = a.data.find((x) => x.id === selected.id);
                setSelected(cur || null);
            }
        } catch (e) { setError(formatApiError(e)); }
    }

    useEffect(() => {
        if (!selected) { setSelectedQuality(null); return; }
        let cancel = false;
        (async () => {
            try {
                const { data } = await http.get(`/admin/partners/${selected.id}/quality`);
                if (!cancel) setSelectedQuality(data);
            } catch (e) { if (!cancel) setSelectedQuality(null); }
        })();
        return () => { cancel = true; };
    }, [selected]);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    async function setStatus(id, status) {
        try {
            await http.post(`/admin/partners/${id}/status`, { status });
            await load();
        } catch (e) { setError(formatApiError(e)); }
    }

    async function remind(jobId) {
        setReminding(jobId); setError("");
        try {
            await http.post(`/admin/partner-jobs/${jobId}/remind`);
            alert("Reminder sent.");
        } catch (e) { setError(formatApiError(e)); }
        finally { setReminding(null); }
    }

    return (
        <div data-testid="admin-partners-page" className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
            <div className="label-eyebrow mb-3">/ Butler Desk</div>
            <h1 className="font-serif text-4xl mb-8">Partners</h1>
            {error && <div className="text-red-400 text-sm mb-6">{error}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <Stat label="Applications" value={apps.length} accent="text-gold" />
                <Stat label="Commissions owed" value={`$${jobsData.total_owed.toFixed(2)}`} accent="text-amber-300" />
                <Stat label="Commissions collected" value={`$${jobsData.total_collected.toFixed(2)}`} accent="text-emerald-300" />
            </div>

            <div className="flex gap-2 mb-6 border-b border-white/5">
                <TabBtn active={tab === "applications"} onClick={() => setTab("applications")} testid="tab-apps">Applications ({apps.length})</TabBtn>
                <TabBtn active={tab === "jobs"} onClick={() => setTab("jobs")} testid="tab-jobs">Reported Jobs ({jobsData.jobs.length})</TabBtn>
            </div>

            {tab === "applications" ? (
                <div className="grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7">
                        {apps.length === 0 ? (
                            <Empty>No applications yet. Share <a href="/partners" className="gold-text underline">/partners</a> with contractors.</Empty>
                        ) : (
                            <div className="bg-slate-900/40 border border-white/5 rounded-sm overflow-hidden">
                                <div className="grid grid-cols-12 text-xs uppercase tracking-widest text-slate-500 border-b border-white/5 px-4 py-3">
                                    <div className="col-span-5">Contractor</div>
                                    <div className="col-span-4">Services</div>
                                    <div className="col-span-3 text-right">Status</div>
                                </div>
                                {apps.map((a) => (
                                    <button
                                        key={a.id}
                                        data-testid={`partner-row-${a.id}`}
                                        onClick={() => setSelected(a)}
                                        className={`w-full text-left grid grid-cols-12 px-4 py-3 border-b border-white/5 hover:bg-slate-900/60 ${selected?.id === a.id ? "bg-slate-900/80" : ""}`}
                                    >
                                        <div className="col-span-5">
                                            <div className="text-sm">{a.contractor_name}</div>
                                            <div className="text-xs text-slate-500">{a.email}</div>
                                        </div>
                                        <div className="col-span-4 text-xs text-slate-400 truncate">{a.services}</div>
                                        <div className="col-span-3 text-right">
                                            <span className={`text-xs px-2 py-1 rounded-sm border ${STATUS_COLORS[a.status] || ""}`}>
                                                {a.status}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <aside className="lg:col-span-5">
                        {selected ? (
                            <div className="bg-slate-900/60 border border-white/10 p-6 rounded-sm sticky top-24 space-y-3">
                                <div className="font-serif text-2xl">{selected.contractor_name}</div>
                                <div className="text-xs text-slate-500">{selected.business_name || "—"}</div>
                                <div className="text-sm text-slate-300 space-y-1 mt-3">
                                    <div>📧 {selected.email}</div>
                                    <div>📞 {selected.phone}</div>
                                    <div>📍 {selected.address}</div>
                                    <div className="text-slate-500">EIN/SSN last-4: {selected.ein_last4}</div>
                                    <div className="pt-2"><strong className="text-gold">Services:</strong> {(selected.service_categories && selected.service_categories.length) ? selected.service_categories.join(", ") : selected.services}</div>
                                    <div><strong className="text-gold">Area:</strong> {(selected.service_cities && selected.service_cities.length) ? selected.service_cities.join(", ") : selected.service_area}</div>
                                    {selected.bio && <div className="text-slate-400 italic pt-1">"{selected.bio}"</div>}
                                    {(selected.years_in_business || selected.insurance_status) && (
                                        <div className="text-xs text-slate-500 pt-1">
                                            {selected.years_in_business ? `${selected.years_in_business} yrs in business` : ""}
                                            {selected.years_in_business && selected.insurance_status ? " · " : ""}
                                            {selected.insurance_status === "yes" ? `Insured (${selected.insurance_carrier || "carrier on file"})` : ""}
                                        </div>
                                    )}
                                    {(selected.state_license_number || selected.county_license_number || selected.local_license_number) && (
                                        <div className="text-xs text-slate-500">
                                            Licenses: {[selected.state_license_number && `ST ${selected.state_license_number}`, selected.county_license_number && `CO ${selected.county_license_number}`, selected.local_license_number && `LO ${selected.local_license_number}`].filter(Boolean).join(" · ")}
                                        </div>
                                    )}
                                    <div className="text-xs text-slate-500 pt-2">Signed: {new Date(selected.signed_at).toLocaleString()}</div>
                                </div>

                                {selectedQuality && (
                                    <div data-testid="admin-quality-block" className="mt-4 pt-3 border-t border-white/5">
                                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Quality score</div>
                                        <div className="flex items-baseline gap-2">
                                            <span data-testid="admin-quality-score" className="font-serif text-3xl text-gold">{selectedQuality.score}</span>
                                            <span className="text-sm text-slate-500">/ 100</span>
                                            {selectedQuality.badges?.top_rated && (
                                                <span data-testid="admin-top-rated-badge" className="ml-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-gold/15 border border-gold/40 text-gold">
                                                    🏆 Top-rated
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            Reviews {selectedQuality.breakdown.reviews.earned}/{selectedQuality.breakdown.reviews.max} · Photos {selectedQuality.breakdown.photos.earned}/{selectedQuality.breakdown.photos.max} · Licensing {selectedQuality.breakdown.licensing.earned}/{selectedQuality.breakdown.licensing.max} · Experience {selectedQuality.breakdown.experience.earned}/{selectedQuality.breakdown.experience.max}
                                            {selectedQuality.badges && (
                                                <> · ⭐ {selectedQuality.badges.five_star_count}</>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <p className="text-xs text-slate-500 italic">Approving will email an invitation to set their password, with Butler Room's W-9 PDF attached for their accountant.</p>

                                <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
                                    <button data-testid="partner-approve" onClick={() => setStatus(selected.id, "approved")} className="btn-gold text-xs px-3 py-2">Approve & invite</button>
                                    <button data-testid="partner-pending" onClick={() => setStatus(selected.id, "pending")} className="btn-ghost text-xs px-3 py-2">Pending</button>
                                    <button data-testid="partner-reject" onClick={() => setStatus(selected.id, "rejected")} className="text-xs px-3 py-2 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition">Reject</button>
                                </div>
                            </div>
                        ) : (
                            <Empty>Select an application to review.</Empty>
                        )}
                    </aside>
                </div>
            ) : (
                jobsData.jobs.length === 0 ? (
                    <Empty>No commissions reported yet. Approved partners will be able to log jobs from their dashboard.</Empty>
                ) : (
                    <div className="bg-slate-900/40 border border-white/5 rounded-sm overflow-hidden">
                        <div className="grid grid-cols-12 text-xs uppercase tracking-widest text-slate-500 border-b border-white/5 px-4 py-3">
                            <div className="col-span-4">Partner / Client</div>
                            <div className="col-span-2">Service date</div>
                            <div className="col-span-2 text-right">Gross</div>
                            <div className="col-span-2 text-right">Commission</div>
                            <div className="col-span-2 text-right">Status</div>
                        </div>
                        {jobsData.jobs.map((j) => (
                            <div key={j.id} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 items-center hover:bg-slate-900/60">
                                <div className="col-span-4">
                                    <div className="text-sm">{j.partner_name}</div>
                                    <div className="text-xs text-slate-500">→ {j.client_name} ({j.is_recurring ? "Recurring" : "One-time"})</div>
                                </div>
                                <div className="col-span-2 text-xs text-slate-400">{j.service_date}</div>
                                <div className="col-span-2 text-right text-sm">${j.gross_amount.toFixed(2)}</div>
                                <div className="col-span-2 text-right gold-text font-serif text-lg">${j.commission_amount.toFixed(2)}</div>
                                <div className="col-span-2 text-right">
                                    {j.payment_status === "paid" ? (
                                        <span className="text-xs px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Paid</span>
                                    ) : (
                                        <button
                                            data-testid={`remind-${j.id}`}
                                            onClick={() => remind(j.id)} disabled={reminding === j.id}
                                            className="text-xs px-3 py-1 rounded-sm border border-white/10 text-slate-300 hover:border-gold hover:text-gold transition disabled:opacity-50"
                                        >
                                            {reminding === j.id ? "Sending…" : "Remind"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className="bg-slate-900/60 border border-white/10 p-5 rounded-sm">
            <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
            <div className={`font-serif text-3xl mt-2 ${accent}`}>{value}</div>
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button data-testid={testid} onClick={onClick}
            className={`text-sm pb-3 px-3 -mb-px border-b-2 ${active ? "border-gold text-gold" : "border-transparent text-slate-400 hover:text-white"}`}>
            {children}
        </button>
    );
}

function Empty({ children }) {
    return <div className="bg-slate-900/40 border border-white/5 p-12 rounded-sm text-center text-slate-500 text-sm">{children}</div>;
}
